import type { ButtonTone } from './button-presets';

/**
 * Every colour a preset emits goes out as `var(--rulecms-button-…, default)`.
 *
 * This is the whole white-label story for buttons. A customer who sets
 *
 *   :root { --rulecms-button-brand: #c8102e; }
 *
 * once in their own stylesheet rebrands every RuleCMS button on the site,
 * without touching a widget, without composer access, and without us shipping
 * a theme provider or a design system to their page. Authors who want one
 * button to differ still override it per column, as before.
 */
export const BUTTON_CSS_VARIABLE_PREFIX = '--rulecms-button';

/**
 * Per-instance slots the library stylesheet reads. The preset writes these on
 * the element; a column `backgroundColor` rewrite and a future widget-level
 * token both land on the same names.
 */
export const BUTTON_FILL_VARIABLE = `${BUTTON_CSS_VARIABLE_PREFIX}-fill`;
export const BUTTON_FILL_HOVER_VARIABLE = `${BUTTON_CSS_VARIABLE_PREFIX}-fill-hover`;
export const BUTTON_FILL_ACTIVE_VARIABLE = `${BUTTON_CSS_VARIABLE_PREFIX}-fill-active`;
export const BUTTON_INK_VARIABLE = `${BUTTON_CSS_VARIABLE_PREFIX}-ink`;
export const BUTTON_INK_HOVER_VARIABLE = `${BUTTON_CSS_VARIABLE_PREFIX}-ink-hover`;
export const BUTTON_BORDER_VARIABLE = `${BUTTON_CSS_VARIABLE_PREFIX}-border`;
export const BUTTON_RING_VARIABLE = `${BUTTON_CSS_VARIABLE_PREFIX}-ring`;

/**
 * The suffixes on a tone's custom properties. `''` is the tone's main colour,
 * e.g. `--rulecms-button-brand`.
 */
export type ToneVariableSlot =
  | ''
  | 'hover'
  | 'active'
  | 'contrast'
  | 'border'
  | 'soft'
  | 'soft-hover'
  | 'soft-contrast';

export const toneCssVariable = (tone: ButtonTone, slot: ToneVariableSlot = ''): string =>
  slot === ''
    ? `${BUTTON_CSS_VARIABLE_PREFIX}-${tone}`
    : `${BUTTON_CSS_VARIABLE_PREFIX}-${tone}-${slot}`;

/**
 * A `var()` reference carrying our default as the fallback, so the button is
 * fully styled whether or not the host defines anything.
 */
export const cssVarWithFallback = (variableName: string, fallback: string): string =>
  `var(${variableName}, ${fallback})`;

/** Shorthand for the common case: one tone slot with its palette default. */
export const toneColor = (
  tone: ButtonTone,
  slot: ToneVariableSlot,
  fallback: string
): string => cssVarWithFallback(toneCssVariable(tone, slot), fallback);
